import { ArrowRight, CircleInfoFill, Xmark } from '@gravity-ui/icons';
import { Button, Icon, Modal } from '@gravity-ui/uikit';

import { useGetSectionsQuery } from 'store/api';
import type { Problem } from 'store/types/common';

import styles from './styles.module.scss';

interface ProblemModalProps {
	openProblemModal: boolean;
	setOpenProblemModal: (open: boolean) => void;
	onSubmit?: () => void;
	problems: Problem[];
}

export const ProblemModal = ({ openProblemModal, setOpenProblemModal, onSubmit, problems }: ProblemModalProps) => {
	const { data: sections } = useGetSectionsQuery();

	const getSectionName = (sectionName: string): string => {
		const section = sections?.find((section) => section.sectionName === sectionName);
		return section ? section.sectionName : '';
	};

	const getSubsectionName = (subsection: { id?: number; sectionName: string }): string => {
		return subsection ? subsection.sectionName : '';
	};

	const sortedProblems = problems?.slice().sort((a, b) => {
		const importanceDiff = b.importance - a.importance;
		if (importanceDiff !== 0) {
			return importanceDiff;
		}

		return (a.id || 0) - (b.id || 0);
	});

	const topProblems = sortedProblems?.length > 5 ? sortedProblems.slice(0, 5) : sortedProblems;

	const groupedProblems = topProblems?.reduce(
		(acc, problem) => {
			const sectionName = problem.parentSectionName || '';
			if (!acc[sectionName]) {
				acc[sectionName] = [];
			}
			acc[sectionName].push(problem);

			return acc;
		},
		{} as Record<string, Problem[]>,
	);

	return (
		<Modal open={openProblemModal} className={styles.modal}>
			<div className={styles.modal__title}>
				<h3>
					{' '}
					Уточните, самые ли важные эти {topProblems?.length} проблем
					{topProblems?.length === 1 ? 'а' : topProblems?.length < 5 ? 'ы' : ''}?
				</h3>
				<Xmark onClick={() => setOpenProblemModal(false)} cursor="pointer" />
			</div>
			<div className={styles.modal__content}>
				<CircleInfoFill />
				<p>
					Обсудите с клиентом {topProblems?.length} наиболее важных для него проблем
					{topProblems?.length === 1 ? 'а' : topProblems?.length < 5 ? 'ы' : ''}, которые отобразились в
					таблице ниже по результатам оценки важности. Если требуется корректировка — вернитесь на шаг назад.
				</p>
			</div>
			<div className={styles.modal__table_container}>
				<table>
					<thead>
						<tr>
							<th className={styles.modal__table_container__section}>Раздел</th>
							<th className={styles.modal__table_container__subsection}>Подраздел</th>
							<th className={styles.modal__table_container__problem}>Описание проблемы</th>
							<th className={styles.modal__table_container__importance}>Оценка</th>
						</tr>
					</thead>
					<tbody>
						{groupedProblems &&
							Object.entries(groupedProblems).map(([sectionName, problems]) => {
								return (
									<>
										{problems.map((problem, index) => (
											<tr key={problem.id || index}>
												{index === 0 && (
													<td rowSpan={problems.length} scope="col">
														{getSectionName(problem.parentSectionName)}
													</td>
												)}
												<td>{getSubsectionName(problem.subsection)}</td>
												<td>{problem.text}</td>
												<td scope="row">{problem.importance}</td>
											</tr>
										))}
									</>
								);
							})}
					</tbody>
				</table>
			</div>
			<div className={styles.modal__btns}>
				<Button
					onClick={() => setOpenProblemModal(false)}
					size="xl"
					view="flat"
					className={styles.modal__btns__change}
				>
					{' '}
					Изменить
				</Button>
				<Button size="xl" className={styles.modal__btns__next} onClick={onSubmit}>
					Перейти далее <Icon data={ArrowRight} size={18} />
				</Button>
			</div>
		</Modal>
	);
};
